import axios from 'axios';
import { authFetch } from './authFetch.js';
import * as units from './units.js';

/**
 * RouteService — Phase 40 update
 * 
 * Primary: AWS Location Service via /api/rv-route (truck-mode, RV dimensions).
 * Fallback: OSRM (car-mode, no restrictions) if backend unavailable.
 * 
 * Output shape unchanged — all downstream consumers (wind, elevation,
 * fuel stops, campgrounds, multi-day) receive the same coordinates format.
 */
export class RouteService {
    constructor() {
        this.backendUrl = import.meta.env.VITE_BACKEND_URL || 'https://6wdv4xnpei.execute-api.us-west-1.amazonaws.com/prod';
    }

    /**
     * Calculate multiple route options with RV-specific considerations.
     * Tries AWS Location Service first (truck mode with rig dimensions),
     * falls back to OSRM if the backend call fails.
     * 
     * @param {string} origin - Starting location text
     * @param {string} destination - Ending location text
     * @param {Object} rvSpecs - RV specifications (height, weight, length)
     * @returns {Promise<{routes: Array, startAddress: string, endAddress: string}>}
     */
    async calculateRoute(origin, destination, rvSpecs) {
        // Clear any AWS response cached by a previous, unrelated calculation.
        // getCachedRouteForNavigation() hands this straight to Ferrostar as
        // authoritative route data — if this run falls back to OSRM below
        // without a fresh AWS response, that old data must not survive to
        // look like it belongs to this route. (Bug: a stale AWS response
        // from a past calculation was silently reused for navigation when
        // this run's AWS call failed and fell back to OSRM.)
        this._lastAWSResponse = null;

        try {
            // Phase 4.5: Use resolved coordinates from autocomplete if available
            const resolvedStart = window.autocompleteService?.getResolvedPlace('start');
            const resolvedDest = window.autocompleteService?.getResolvedPlace('destination');

            let startCoords, endCoords;

            if (resolvedStart) {
                startCoords = { lat: resolvedStart.lat, lng: resolvedStart.lng };
                console.log('Using autocomplete coords for start:', startCoords);
            } else {
                console.log('Geocoding start location...');
                startCoords = await this.geocodeLocation(origin);
            }

            if (resolvedDest) {
                endCoords = { lat: resolvedDest.lat, lng: resolvedDest.lng };
                console.log('Using autocomplete coords for destination:', endCoords);
            } else {
                console.log('Geocoding destination...');
                endCoords = await this.geocodeLocation(destination);
            }

            console.log('Start:', startCoords, 'End:', endCoords);

            // Try AWS Location Service (truck mode) first
            let routes;
            try {
                routes = await this.getAWSRoutes(startCoords, endCoords, rvSpecs, origin, destination);
                console.log(`🚛 AWS Location Service: ${routes.length} route(s) returned (truck mode)`);
            } catch (awsErr) {
                console.warn('AWS routing failed, falling back to OSRM:', awsErr.message);
                routes = await this.getOSRMRoutes(startCoords, endCoords);
                console.log(`🗺️ OSRM fallback: ${routes.length} route(s) returned (car mode)`);
            }

            // Return all routes with metadata
            return {
                routes: routes.map((route, index) => ({
                    id: route.id !== undefined ? route.id : index,
                    distance: route.distance,
                    distanceValue: route.distanceValue,
                    duration: route.duration,
                    durationValue: route.durationValue,
                    coordinates: route.coordinates,
                    startAddress: origin,
                    endAddress: destination,
                    highways: route.highways || [],
                    legs: route.legs || [],
                    truckMode: route.truckMode || false,
                    dataSource: route.dataSource || 'osrm',
                    warnings: this.checkRVRestrictions(null, rvSpecs),
                    // AWS's own record of whether it could find a route that
                    // actually respects the truck dimensions sent -- see
                    // lambda/rv-route/routesV2.mjs's restrictionWarnings.
                    restrictionWarnings: route.restrictionWarnings || []
                })),
                startAddress: origin,
                endAddress: destination
            };
        } catch (error) {
            console.error('Error calculating route:', error);
            throw error;
        }
    }

    // ─────────────────────────────────────────────────────────────────────
    // AWS LOCATION SERVICE (PRIMARY — Phase 40)
    // ─────────────────────────────────────────────────────────────────────

    /**
     * Get routes from AWS Location Service via the rv-route Lambda.
     * Uses authFetch for authenticated users (truck mode) or
     * regular fetch for the public endpoint (car mode fallback).
     */
    async getAWSRoutes(start, end, rvSpecs, originText, destText) {
        // Build rig profile from rvSpecs for the backend
        const rigProfile = this.buildRigProfile(rvSpecs);

        const payload = {
            origin: { lat: start.lat, lng: start.lng },
            destination: { lat: end.lat, lng: end.lng },
            startAddress: originText,
            endAddress: destText,
            rigProfile,
            alternatives: true,
            units: units.isMetric() ? 'metric' : 'imperial'
        };
        // Added stops: the route passes through each, in order (rv-route's
        // preferences.use = AWS PassThrough waypoints, no leg split).
        const stops = this.stopsForNextRoute || [];
        this.stopsForNextRoute = null;
        // Avoid cities: a standing preference (UIController.avoidCities),
        // not cleared here -- unlike stops, it should still apply on the
        // NEXT calculation too, not just this one.
        const avoidCities = this.avoidCities || [];
        // Through cities: same standing-preference timing as avoidCities,
        // but feeds preferences.use -- merged with stops below, not a
        // separate preferences key (rv-route only has use/avoid).
        const throughCities = this.throughCities || [];

        const useWaypoints = [
            ...stops.map(s => ({ lat: s.lat, lng: s.lng, label: s.name })),
            ...throughCities.map(c => ({ lat: c.lat, lng: c.lng, label: c.label }))
        ];

        const preferences = {};
        if (useWaypoints.length > 0) {
            preferences.use = useWaypoints;
            // Forcing the route through specific points collapses route
            // choice the same way stops always has -- alternatives off
            // whenever either source of a use-waypoint is present.
            payload.alternatives = false;
        }
        if (avoidCities.length > 0) {
            preferences.avoid = avoidCities.map(c => ({ bbox: c.bbox, label: c.label }));
        }
        if (Object.keys(preferences).length > 0) payload.preferences = preferences;

        // Android app: remember this rig/units for trips started from Android
        // Auto (native ZephyrTripDefaults). Not for car mode, which plans
        // without a rig. Older app versions don't have setTripDefaults.
        if (window.routeMode !== 'car' && rigProfile) {
            try {
                window.Capacitor?.Plugins?.ZephyrNavigation?.setTripDefaults?.({
                    rigProfile: JSON.stringify(rigProfile),
                    units: payload.units,
                    userPlan: window.routeTrackingService?.launchStatus?.plan || ''
                })?.catch?.(() => {});
            } catch (e) { /* not the Android app */ }
        }

        let response;

        // Try authenticated endpoint first (truck mode with dimensions)
        // Car mode bypasses truck routing entirely
        const isAuthenticated = !!window.authService?.getAccessToken();
        const useCarMode = window.routeMode === 'car';
        if (isAuthenticated && !useCarMode) {
            try {
                response = await authFetch(`${this.backendUrl}/api/rv-route`, {
                    method: 'POST',
                    body: JSON.stringify(payload)
                });
            } catch (authErr) {
                console.warn('Authenticated rv-route failed, trying public:', authErr.message);
                response = null;
            }
        }

        // Fall back to public endpoint (car mode, no auth needed)
        if (!response || !response.ok) {
            response = await fetch(`${this.backendUrl}/api/rv-route/public`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
        }

        if (!response.ok) {
            throw new Error(`AWS route API returned ${response.status}`);
        }

        const data = await response.json();

        if (data.status !== 'success' || !data.routes || data.routes.length === 0) {
            throw new Error(data.message || 'No routes returned from AWS');
        }

        // Phase 40B: Cache raw AWS response for Ferrostar navigation handoff
        this._lastAWSResponse = data;

        return data.routes;
    }

    /**
     * Convert frontend rvSpecs to the rigProfile format the Lambda expects.
     */
    buildRigProfile(rvSpecs) {
        if (!rvSpecs) return null;

        return {
            rigType: rvSpecs.rvType || rvSpecs.rigType || null,
            heightFt: rvSpecs.height || rvSpecs.heightFt || null,
            heightInches: rvSpecs.heightInches || null,
            widthFt: rvSpecs.width || rvSpecs.widthFt || null,
            lengthFt: rvSpecs.length || rvSpecs.lengthFt || null,
            grossVehicleWeightLbs: rvSpecs.weight || rvSpecs.grossVehicleWeightLbs || rvSpecs.gvwrLbs || null
        };
    }

    // ─────────────────────────────────────────────────────────────────────
    // OSRM FALLBACK (legacy — used when AWS endpoint unavailable)
    // ─────────────────────────────────────────────────────────────────────

    /**
     * Get multiple route alternatives from OSRM (Open Source Routing Machine)
     */
    async getOSRMRoutes(start, end) {
        try {
            const routes = [];

            const url = `https://router.project-osrm.org/route/v1/driving/${start.lng},${start.lat};${end.lng},${end.lat}`;

            const response = await axios.get(url, {
                params: {
                    overview: 'full',
                    geometries: 'geojson',
                    steps: 'true',
                    alternatives: 'true',
                    continue_straight: 'false'
                }
            });

            if (response.data.code === 'Ok' && response.data.routes.length > 0) {
                response.data.routes.forEach(route => {
                    routes.push(this.processOSRMRoute(route));
                });
            }

            // If we only got one route, generate alternatives via waypoints
            if (routes.length === 1) {
                console.log('Only one OSRM route, generating alternatives...');
                const alternatives = await this.generateAlternativeRoutes(start, end);
                routes.push(...alternatives);
            }

            return routes;
        } catch (error) {
            console.error('OSRM routing error:', error);
            throw error;
        }
    }

    /**
     * Process a single OSRM route
     */
    processOSRMRoute(route) {
        const coordinates = route.geometry.coordinates.map(coord => ({
            lat: coord[1],
            lng: coord[0]
        }));

        const distanceMiles = route.distance / 1609.34;

        let durationHours = Math.floor(route.duration / 3600);
        let durationMinutes = Math.round((route.duration % 3600) / 60);
        if (durationMinutes === 60) {
            durationHours += 1;
            durationMinutes = 0;
        }

        return {
            distance: `${Math.round(distanceMiles)} miles`,
            distanceValue: distanceMiles,
            duration: `${durationHours} hours ${durationMinutes} min`,
            durationValue: route.duration / 3600,
            coordinates: coordinates,
            highways: this.extractHighwayRefs(route),
            truckMode: false,
            dataSource: 'osrm'
        };
    }

    /**
     * Extract highway reference names from OSRM route steps.
     */
    extractHighwayRefs(route) {
        const refs = new Set();
        if (!route.legs) return [];
        for (const leg of route.legs) {
            if (!leg.steps) continue;
            for (const step of leg.steps) {
                if (!step.ref) continue;
                const parts = step.ref.split(';');
                for (const part of parts) {
                    const trimmed = part.trim();
                    const match = trimmed.match(/^(I|US|SR|KY|OH|IN|TN|GA|FL|TX|CA|CO|WY|UT|MT|ID|NV|AZ|NM|OR|WA|PA|NY|NC|SC|VA|WV|MO|IL|MI|MN|WI|IA|NE|KS|OK|AR|LA|MS|AL|ND|SD|CT|MA|NH|VT|ME|RI|NJ|DE|MD|HI|AK)\s*(\d+)/i);
                    if (match) {
                        const prefix = match[1].toUpperCase();
                        const num = match[2];
                        if (prefix === 'I') refs.add(`I-${num}`);
                        else if (prefix === 'US') refs.add(`US-${num}`);
                        else refs.add(`SR-${num}`);
                    }
                }
            }
        }
        return Array.from(refs);
    }

    /**
     * Generate alternative routes by using different waypoints (OSRM fallback)
     */
    async generateAlternativeRoutes(start, end) {
        const alternatives = [];

        try {
            const midLat = (start.lat + end.lat) / 2;
            const midLng = (start.lng + end.lng) / 2;

            const latDiff = end.lat - start.lat;
            const lngDiff = end.lng - start.lng;
            const offsetFactor = 0.15;

            const waypoint1 = {
                lat: midLat + (lngDiff * offsetFactor),
                lng: midLng - (latDiff * offsetFactor)
            };

            const waypoint2 = {
                lat: midLat - (lngDiff * offsetFactor),
                lng: midLng + (latDiff * offsetFactor)
            };

            try {
                const route1 = await this.getRouteViaWaypoint(start, waypoint1, end);
                if (route1 && this.isRouteDifferent(route1, alternatives)) {
                    alternatives.push(route1);
                }
            } catch (e) {
                console.log('OSRM alternative 1 failed:', e.message);
            }

            try {
                const route2 = await this.getRouteViaWaypoint(start, waypoint2, end);
                if (route2 && this.isRouteDifferent(route2, alternatives)) {
                    alternatives.push(route2);
                }
            } catch (e) {
                console.log('OSRM alternative 2 failed:', e.message);
            }
        } catch (error) {
            console.error('Error generating OSRM alternatives:', error);
        }

        return alternatives;
    }

    /**
     * Get route through a waypoint (OSRM)
     */
    async getRouteViaWaypoint(start, waypoint, end) {
        const url = `https://router.project-osrm.org/route/v1/driving/${start.lng},${start.lat};${waypoint.lng},${waypoint.lat};${end.lng},${end.lat}`;

        const response = await axios.get(url, {
            params: { overview: 'full', geometries: 'geojson' }
        });

        if (response.data.code === 'Ok' && response.data.routes.length > 0) {
            return this.processOSRMRoute(response.data.routes[0]);
        }
        return null;
    }

    /**
     * Check if a route is significantly different from existing routes
     */
    isRouteDifferent(newRoute, existingRoutes) {
        if (existingRoutes.length === 0) return true;
        for (const route of existingRoutes) {
            const percentDiff = (Math.abs(newRoute.distanceValue - route.distanceValue) / route.distanceValue) * 100;
            if (percentDiff < 5) return false;
        }
        return true;
    }

    // ─────────────────────────────────────────────────────────────────────
    // SHARED UTILITIES
    // ─────────────────────────────────────────────────────────────────────

    /**
     * Geocode a location using Nominatim (free, no API key required)
     */
    async geocodeLocation(location) {
        try {
            const sanitized = this.sanitizeLocation(location);

            const response = await axios.get('https://nominatim.openstreetmap.org/search', {
                params: {
                    q: sanitized,
                    format: 'json',
                    limit: 1,
                    countrycodes: 'us'
                },
                headers: { 'User-Agent': 'Zephyr-RV-Navigator' }
            });

            if (response.data && response.data.length > 0) {
                return {
                    lat: parseFloat(response.data[0].lat),
                    lng: parseFloat(response.data[0].lon)
                };
            } else {
                throw new Error('Location not found');
            }
        } catch (error) {
            console.error('Geocoding error:', error);
            throw error;
        }
    }

    /**
     * Sanitize location input — expand state abbreviations for better geocoding
     */
    sanitizeLocation(input) {
        const STATE_MAP = {
            'AL': 'Alabama', 'AK': 'Alaska', 'AZ': 'Arizona', 'AR': 'Arkansas',
            'CA': 'California', 'CO': 'Colorado', 'CT': 'Connecticut', 'DE': 'Delaware',
            'FL': 'Florida', 'GA': 'Georgia', 'HI': 'Hawaii', 'ID': 'Idaho',
            'IL': 'Illinois', 'IN': 'Indiana', 'IA': 'Iowa', 'KS': 'Kansas',
            'KY': 'Kentucky', 'LA': 'Louisiana', 'ME': 'Maine', 'MD': 'Maryland',
            'MA': 'Massachusetts', 'MI': 'Michigan', 'MN': 'Minnesota', 'MS': 'Mississippi',
            'MO': 'Missouri', 'MT': 'Montana', 'NE': 'Nebraska', 'NV': 'Nevada',
            'NH': 'New Hampshire', 'NJ': 'New Jersey', 'NM': 'New Mexico', 'NY': 'New York',
            'NC': 'North Carolina', 'ND': 'North Dakota', 'OH': 'Ohio', 'OK': 'Oklahoma',
            'OR': 'Oregon', 'PA': 'Pennsylvania', 'RI': 'Rhode Island', 'SC': 'South Carolina',
            'SD': 'South Dakota', 'TN': 'Tennessee', 'TX': 'Texas', 'UT': 'Utah',
            'VT': 'Vermont', 'VA': 'Virginia', 'WA': 'Washington', 'WV': 'West Virginia',
            'WI': 'Wisconsin', 'WY': 'Wyoming', 'DC': 'District of Columbia'
        };

        let location = input.trim();

        const patterns = [
            /^(.+?),?\s+([A-Za-z]{2})\s+(\d{5}(?:-\d{4})?)$/,
            /^(.+?),?\s+([A-Za-z]{2})$/
        ];

        for (const pattern of patterns) {
            const match = location.match(pattern);
            if (match) {
                const city = match[1].replace(/,\s*$/, '').trim();
                const stateAbbr = match[2].toUpperCase();
                const zip = match[3] || '';

                if (STATE_MAP[stateAbbr]) {
                    location = `${city}, ${STATE_MAP[stateAbbr]}`;
                    if (zip) location += ` ${zip}`;
                    console.log(`📍 Sanitized: "${input}" → "${location}"`);
                    return location;
                }
            }
        }

        return location;
    }

    /**
     * Check for RV-specific restrictions (client-side warnings)
     */
    checkRVRestrictions(route, rvSpecs) {
        const warnings = [];

        if (rvSpecs.height > 13) {
            warnings.push({
                type: 'height', severity: 'high',
                message: `Your RV height (${units.isMetric() ? units.height(rvSpecs.height, 'up').toFixed(1) + ' m' : +Number(rvSpecs.height).toFixed(2) + "'"}) exceeds standard clearances. Watch for low bridges and tunnels.`
            });
        } else if (rvSpecs.height > 11) {
            warnings.push({
                type: 'height', severity: 'moderate',
                message: `Your RV height (${units.isMetric() ? units.height(rvSpecs.height, 'up').toFixed(1) + ' m' : +Number(rvSpecs.height).toFixed(2) + "'"}) may encounter clearance issues on some routes.`
            });
        }

        if (rvSpecs.weight > 26000) {
            warnings.push({
                type: 'weight', severity: 'moderate',
                message: `Your RV weight (${units.isMetric() ? units.weight(rvSpecs.weight).toLocaleString('en-AU') + ' kg' : Math.round(Number(rvSpecs.weight)).toLocaleString() + ' lbs'}) may restrict access to some bridges and roads.`
            });
        }

        if (rvSpecs.length > 35) {
            warnings.push({
                type: 'length', severity: 'moderate',
                message: `Your RV length (${units.isMetric() ? units.length(rvSpecs.length).toFixed(1) + ' m' : +Number(rvSpecs.length).toFixed(2) + "'"}) may make some turns and parking difficult.`
            });
        }

        return warnings;
    }

    // ─────────────────────────────────────────────────────────────────────
    // ROUTE CACHE — Phase 40B (for Ferrostar navigation handoff)
    // ─────────────────────────────────────────────────────────────────────

    /**
     * Get the cached AWS route response for handing off to Ferrostar navigation.
     * Returns the full response including route geometry needed for turn-by-turn.
     * @param {number} [routeIndex=0] - Which route candidate to retrieve
     * @returns {Object|null} - The cached route data or null if not available
     */
    getCachedRouteForNavigation(routeIndex = 0) {
        if (!this._lastAWSResponse || !this._lastAWSResponse.routes) return null;
        const route = this._lastAWSResponse.routes[routeIndex];
        if (!route) return null;

        return {
            route,
            truckMode: this._lastAWSResponse.truckMode,
            dimensions: this._lastAWSResponse.dimensions,
            dataSource: this._lastAWSResponse.dataSource,
            preferencesApplied: this._lastAWSResponse.preferencesApplied || null
        };
    }

    /**
     * Get the full cached AWS response (all routes).
     * Used by NavigationService to prepare the handoff payload.
     * @returns {Object|null}
     */
    getCachedAWSResponse() {
        return this._lastAWSResponse || null;
    }

    /**
     * Calculate haversine distance between two points (miles)
     */
    calculateDistance(lat1, lon1, lat2, lon2) {
        const R = 3959;
        const dLat = this.toRad(lat2 - lat1);
        const dLon = this.toRad(lon2 - lon1);
        const a =
            Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(this.toRad(lat1)) * Math.cos(this.toRad(lat2)) *
            Math.sin(dLon / 2) * Math.sin(dLon / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        return R * c;
    }

    toRad(degrees) {
        return degrees * (Math.PI / 180);
    }

    /**
     * Decode Google Maps polyline (legacy — kept for potential future use)
     */
    decodePolyline(encoded) {
        const coordinates = [];
        let index = 0;
        let lat = 0;
        let lng = 0;

        while (index < encoded.length) {
            let b, shift = 0, result = 0;
            do {
                b = encoded.charCodeAt(index++) - 63;
                result |= (b & 0x1f) << shift;
                shift += 5;
            } while (b >= 0x20);
            lat += ((result & 1) ? ~(result >> 1) : (result >> 1));

            shift = 0; result = 0;
            do {
                b = encoded.charCodeAt(index++) - 63;
                result |= (b & 0x1f) << shift;
                shift += 5;
            } while (b >= 0x20);
            lng += ((result & 1) ? ~(result >> 1) : (result >> 1));

            coordinates.push({ lat: lat / 1e5, lng: lng / 1e5 });
        }
        return coordinates;
    }
}
